import { useMemo } from 'react';
import { Node } from '@xyflow/react';
import {
    CustomNodeData,
    LayoutNode,
    CanvasBounds,
    ClusterCacheEntry,
    NamespaceCacheEntry,
    AwsAccountCacheEntry,
    OciTenancyCacheEntry,
    ExpansionViewState,
    VPCResourceGroup,
    VCNResourceGroup,
    SubnetResourceGroup,
    groupResourcesBySubnet,
} from '../types';
import { NODE_SIZES, LAYOUT } from '../constants';
import { InventoryCluster, AWSConnection, OCIConnection, FlowSummary } from '../../../api/client';

// ============================================================================
// TYPES
// ============================================================================

export interface LayoutEngineInput {
    clusters: InventoryCluster[];
    viewState: ExpansionViewState;
    clusterCache: Map<string, ClusterCacheEntry>;
    namespaceCache: Map<string, NamespaceCacheEntry>;
    loadingNodes: Set<string>;
    // AWS support
    awsConnections?: AWSConnection[];
    awsAccountCache?: Map<string, AwsAccountCacheEntry>;
    // OCI support
    ociConnections?: OCIConnection[];
    ociTenancyCache?: Map<string, OciTenancyCacheEntry>;
}

export interface LayoutEngineOutput {
    nodes: Node<CustomNodeData>[];
    canvasBounds: CanvasBounds;
}

// ============================================================================
// HELPER: Calculate grid layout config
// ============================================================================

function getGridConfig(itemCount: number): { itemsPerRow: number; rows: number } {
    if (itemCount === 0) return { itemsPerRow: 1, rows: 0 };

    const optimalPerRow = Math.ceil(Math.sqrt(itemCount));

    // Scale the max columns based on item count to avoid extremely tall containers.
    // Small containers (≤36 items): up to 6 columns (original behavior)
    // Medium containers (37-100 items): up to 10 columns
    // Large containers (100-500 items): up to 14 columns
    // Very large containers (500+ items): up to 20 columns
    let maxCols: number = LAYOUT.maxItemsPerRow;
    if (itemCount > 400) {
        maxCols = 20;
    } else if (itemCount > 100) {
        maxCols = 14;
    } else if (itemCount > 64) {
        maxCols = 10;
    }

    const itemsPerRow = Math.max(LAYOUT.minItemsPerRow, Math.min(optimalPerRow, maxCols));
    const rows = Math.ceil(itemCount / itemsPerRow);

    return { itemsPerRow, rows };
}

// ============================================================================
// HELPER: Calculate child position in grid (simple version for uniform sizes)
// ============================================================================

function calculateGridPosition(
    index: number,
    totalItems: number,
    nodeSize: { width: number; height: number },
    spacing: number
): { x: number; y: number } {
    const { itemsPerRow } = getGridConfig(totalItems);

    const col = index % itemsPerRow;
    const row = Math.floor(index / itemsPerRow);

    const x = LAYOUT.containerPadding + col * (nodeSize.width + spacing);
    const y = LAYOUT.containerPadding + LAYOUT.headerHeight + row * (nodeSize.height + spacing);

    return { x, y };
}

// ============================================================================
// HELPER: Position nodes in grid with variable sizes
// ============================================================================

function positionNodesInVariableGrid(
    nodes: LayoutNode[],
    spacing: number
): void {
    if (nodes.length === 0) return;

    const { itemsPerRow } = getGridConfig(nodes.length);
    const numRows = Math.ceil(nodes.length / itemsPerRow);

    // Calculate max width for each column and max height for each row
    const colWidths: number[] = new Array(itemsPerRow).fill(0);
    const rowHeights: number[] = new Array(numRows).fill(0);

    nodes.forEach((node, index) => {
        const col = index % itemsPerRow;
        const row = Math.floor(index / itemsPerRow);
        colWidths[col] = Math.max(colWidths[col], node.size.width);
        rowHeights[row] = Math.max(rowHeights[row], node.size.height);
    });

    // Calculate cumulative positions
    const colStarts: number[] = [LAYOUT.containerPadding];
    for (let c = 1; c < itemsPerRow; c++) {
        colStarts[c] = colStarts[c - 1] + colWidths[c - 1] + spacing;
    }

    const rowStarts: number[] = [LAYOUT.containerPadding + LAYOUT.headerHeight];
    for (let r = 1; r < numRows; r++) {
        rowStarts[r] = rowStarts[r - 1] + rowHeights[r - 1] + spacing;
    }

    // Position each node
    nodes.forEach((node, index) => {
        const col = index % itemsPerRow;
        const row = Math.floor(index / itemsPerRow);
        node.position = { x: colStarts[col], y: rowStarts[row] };
    });
}

// ============================================================================
// HELPER: Calculate container size from children (with variable sizes)
// ============================================================================

function calculateContainerSize(
    children: Array<{ size: { width: number; height: number } }>,
    containerType: 'cluster' | 'namespace'
): { width: number; height: number } {
    const minSize = containerType === 'cluster'
        ? NODE_SIZES.cluster.expanded
        : NODE_SIZES.namespace.expanded;

    if (children.length === 0) {
        return { width: minSize.minWidth, height: minSize.minHeight };
    }

    const spacing = containerType === 'cluster' ? LAYOUT.namespaceSpacing : LAYOUT.nodeSpacing;
    const { itemsPerRow } = getGridConfig(children.length);
    const numRows = Math.ceil(children.length / itemsPerRow);

    // Calculate max width for each column and max height for each row
    const colWidths: number[] = new Array(itemsPerRow).fill(0);
    const rowHeights: number[] = new Array(numRows).fill(0);

    children.forEach((child, index) => {
        const col = index % itemsPerRow;
        const row = Math.floor(index / itemsPerRow);
        colWidths[col] = Math.max(colWidths[col], child.size.width);
        rowHeights[row] = Math.max(rowHeights[row], child.size.height);
    });

    // Sum up all column widths and row heights
    const totalColWidth = colWidths.reduce((sum, w) => sum + w, 0);
    const totalRowHeight = rowHeights.reduce((sum, h) => sum + h, 0);

    // Calculate content area with spacing between items
    const contentWidth = totalColWidth + (itemsPerRow - 1) * spacing;
    const contentHeight = totalRowHeight + (numRows - 1) * spacing;

    // Add padding and header
    const totalWidth = contentWidth + LAYOUT.containerPadding * 2;
    const totalHeight = contentHeight + LAYOUT.containerPadding * 2 + LAYOUT.headerHeight;

    return {
        width: Math.max(minSize.minWidth, totalWidth),
        height: Math.max(minSize.minHeight, totalHeight)
    };
}

// ============================================================================
// BUILD WORKLOAD LAYOUT NODES
// ============================================================================

function buildWorkloadLayoutNodes(
    namespaceCache: NamespaceCacheEntry
): LayoutNode[] {
    const workloads = namespaceCache.workloads || [];
    const services = namespaceCache.services || [];
    const allItems = [...workloads, ...services];

    if (allItems.length === 0) return [];

    const layoutNodes: LayoutNode[] = [];

    allItems.forEach((item, index) => {
        const isService = !('kind' in item) || item.kind === undefined;
        const nodeType = isService ? 'service' : 'workload';
        const nodeSize = isService ? NODE_SIZES.service : NODE_SIZES.workload;

        const position = calculateGridPosition(
            index,
            allItems.length,
            nodeSize,
            LAYOUT.nodeSpacing
        );

        layoutNodes.push({
            id: item.id || (item as any).uid,
            type: nodeType,
            isExpanded: false,
            isExpandable: false,
            size: nodeSize,
            position,
            data: {
                type: nodeType,
                label: item.name,
                level: 2,
                kind: (item as any).kind,
                namespaceID: item.namespace_id,
                namespaceName: namespaceCache.namespace.name,
                clusterUID: namespaceCache.clusterUID,
                labels: item.labels || {},
                lastSeen: item.created_at,
                isExpandable: false,
                expansionDepth: 2,
                parentNamespaceID: namespaceCache.namespace.id,
                parentClusterUID: namespaceCache.clusterUID,
                // Incident data (from API response)
                incidentCount: (item as any).incident_count,
                incidentSeverity: (item as any).incident_severity,
                // Service-specific
                ...(isService && {
                    serviceType: (item as any).service_type,
                    clusterIp: (item as any).cluster_ip,
                    externalIps: (item as any).external_ips
                })
            }
        });
    });

    return layoutNodes;
}

// ============================================================================
// BUILD NAMESPACE LAYOUT NODE
// ============================================================================

function buildNamespaceLayoutNode(
    namespace: any,
    isExpanded: boolean,
    namespaceCache: NamespaceCacheEntry | undefined,
    isLoading: boolean,
    clusterUID: string
): LayoutNode {
    const namespaceID = namespace.id;

    if (!isExpanded || !namespaceCache?.isComplete) {
        // Collapsed namespace
        return {
            id: namespaceID,
            type: 'namespace',
            isExpanded: false,
            isExpandable: true,
            size: NODE_SIZES.namespace.collapsed,
            position: { x: 0, y: 0 }, // Will be set by parent
            data: {
                type: 'namespace',
                label: namespace.name,
                level: 1,
                namespaceID,
                clusterUID,
                labels: namespace.labels || {},
                lastSeen: namespace.created_at,
                isExpanded: false,
                isExpandable: true,
                isLoading,
                childCount: namespaceCache
                    ? (namespaceCache.workloads?.length || 0) + (namespaceCache.services?.length || 0)
                    : undefined,
                expansionDepth: 1,
                parentClusterUID: clusterUID,
                incidentCount: namespace.incident_count,
                incidentSeverity: namespace.incident_severity
            }
        };
    }

    // Expanded namespace - build children
    const children = buildWorkloadLayoutNodes(namespaceCache);

    // Position children within container first
    const allItems = [...(namespaceCache.workloads || []), ...(namespaceCache.services || [])];
    children.forEach((child, index) => {
        const nodeSize = child.type === 'service' ? NODE_SIZES.service : NODE_SIZES.workload;
        child.position = calculateGridPosition(index, allItems.length, nodeSize, LAYOUT.nodeSpacing);
    });

    // Calculate container size from actual positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.namespace.expanded
    );

    return {
        id: namespaceID,
        type: 'container', // Expanded namespace becomes a container
        isExpanded: true,
        isExpandable: true,
        size: containerSize,
        position: { x: 0, y: 0 }, // Will be set by parent
        children,
        data: {
            type: 'container',
            containerType: 'namespace',
            label: namespace.name,
            level: 1,
            namespaceID,
            clusterUID,
            count: children.length,
            isExpanded: true,
            isExpandable: true,
            expansionDepth: 1,
            parentClusterUID: clusterUID,
            incidentCount: namespace.incident_count,
            incidentSeverity: namespace.incident_severity
        }
    };
}

// ============================================================================
// BUILD CLUSTER LAYOUT NODE
// ============================================================================

function buildClusterLayoutNode(
    cluster: InventoryCluster,
    isExpanded: boolean,
    clusterCache: ClusterCacheEntry | undefined,
    viewState: ExpansionViewState,
    namespaceCache: Map<string, NamespaceCacheEntry>,
    loadingNodes: Set<string>
): LayoutNode {
    const clusterUID = cluster.uid;
    const isLoading = loadingNodes.has(clusterUID);

    if (!isExpanded || !clusterCache?.isComplete) {
        // Collapsed cluster
        return {
            id: clusterUID,
            type: 'cluster',
            isExpanded: false,
            isExpandable: true,
            size: NODE_SIZES.cluster.collapsed,
            position: { x: 0, y: 0 }, // Will be set later
            data: {
                type: 'cluster',
                label: cluster.name,
                level: 0,
                clusterUID,
                isConnected: cluster.is_connected,
                description: cluster.description,
                lastSeen: cluster.created_at,
                isExpanded: false,
                isExpandable: true,
                isLoading,
                childCount: clusterCache?.namespaces?.length,
                expansionDepth: 0,
                // Incident data (from API response)
                incidentCount: cluster.incident_count,
                incidentSeverity: cluster.incident_severity
            }
        };
    }

    // Expanded cluster - build namespace children
    const namespaceNodes: LayoutNode[] = [];

    (clusterCache.namespaces || []).forEach(namespace => {
        const nsExpanded = viewState.expandedNamespaces.has(namespace.id);
        const nsCache = namespaceCache.get(namespace.id);
        const nsLoading = loadingNodes.has(namespace.id);

        const nsNode = buildNamespaceLayoutNode(
            namespace,
            nsExpanded,
            nsCache,
            nsLoading,
            clusterUID
        );

        namespaceNodes.push(nsNode);
    });

    // Position namespaces in grid with variable sizes (accounts for expanded namespaces)
    positionNodesInVariableGrid(namespaceNodes, LAYOUT.namespaceSpacing);

    // Calculate container size from actual positioned children
    // This ensures consistency with AWS/OCI account sizing and properly accounts for expanded namespaces
    const containerSize = calculateContainerSizeFromPositionedChildren(
        namespaceNodes,
        NODE_SIZES.cluster.expanded
    );

    return {
        id: clusterUID,
        type: 'container',
        isExpanded: true,
        isExpandable: true,
        size: containerSize,
        position: { x: 0, y: 0 }, // Will be set later
        children: namespaceNodes,
        data: {
            type: 'container',
            containerType: 'cluster',
            label: cluster.name,
            level: 0,
            clusterUID,
            isConnected: cluster.is_connected,
            count: namespaceNodes.length,
            isExpanded: true,
            isExpandable: true,
            expansionDepth: 0,
            // Incident data (from API response)
            incidentCount: cluster.incident_count,
            incidentSeverity: cluster.incident_severity
        }
    };
}

// ============================================================================
// BUILD AWS RESOURCE LAYOUT NODES
// ============================================================================

function buildAwsResourceLayoutNodes(resources: any[]): LayoutNode[] {
    if (resources.length === 0) return [];

    const layoutNodes: LayoutNode[] = [];

    resources.forEach((resource, index) => {
        const position = calculateGridPosition(
            index,
            resources.length,
            NODE_SIZES.awsResource,
            LAYOUT.nodeSpacing
        );

        layoutNodes.push({
            id: resource.id,
            type: 'aws-resource',
            isExpanded: false,
            isExpandable: false,
            size: NODE_SIZES.awsResource,
            position,
            data: {
                type: 'aws-resource',
                label: resource.name || resource.resource_id,
                level: 2,
                resourceType: resource.resource_type,
                resourceId: resource.resource_id,
                arn: resource.arn,
                awsAccountId: resource.aws_account_id,
                awsRegion: resource.region,
                vpcId: resource.vpc_id,
                subnetId: resource.subnet_id,
                availabilityZone: resource.availability_zone,
                privateIp: resource.private_ip,
                publicIp: resource.public_ip,
                resourceStatus: resource.resource_status,
                isExpandable: false,
                expansionDepth: 2,
                // Incident data (from API response)
                incidentCount: resource.incident_count,
                incidentSeverity: resource.incident_severity,
                // Full CloudResource for detail dialog
                cloudResource: resource
            }
        });
    });

    return layoutNodes;
}

// ============================================================================
// BUILD SUBNET LAYOUT NODE
// ============================================================================

function buildSubnetLayoutNode(
    subnetGroup: SubnetResourceGroup,
    vpcId: string,
    accountId: string
): LayoutNode {
    const subnetId = subnetGroup.subnetId;
    const isNoSubnet = subnetId === 'no-subnet';

    // Build resource children for this subnet
    const children = buildAwsResourceLayoutNodes(subnetGroup.resources);

    // Position children in grid
    children.forEach((child, index) => {
        child.position = calculateGridPosition(
            index,
            subnetGroup.resources.length,
            NODE_SIZES.awsResource,
            LAYOUT.nodeSpacing
        );
    });

    // Calculate container size from positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.awsSubnet.expanded
    );

    // Display name: show subnet ID shortened or "VPC Resources" for no-subnet
    const displayLabel = isNoSubnet
        ? 'VPC Resources'
        : subnetId.length > 20 ? subnetId.substring(0, 17) + '...' : subnetId;

    return {
        id: `subnet:${vpcId}:${subnetId}`,
        type: 'container',
        isExpanded: true,
        isExpandable: false, // Subnets are always expanded
        size: containerSize,
        position: { x: 0, y: 0 },
        children,
        data: {
            type: 'container',
            containerType: 'aws-subnet',
            label: displayLabel,
            level: 2,
            subnetId: isNoSubnet ? undefined : subnetId,
            vpcId,
            awsAccountId: accountId,
            availabilityZone: subnetGroup.availabilityZone,
            count: children.length,
            isExpanded: true,
            isExpandable: false,
            expansionDepth: 2
        }
    };
}

// ============================================================================
// BUILD VPC LAYOUT NODE
// ============================================================================

function buildVpcLayoutNode(
    vpcGroup: VPCResourceGroup,
    isExpanded: boolean,
    accountId: string,
    isLoading: boolean
): LayoutNode {
    const vpcId = vpcGroup.vpcId;

    if (!isExpanded) {
        // Collapsed VPC
        return {
            id: `vpc:${vpcId}`,
            type: 'aws-vpc',
            isExpanded: false,
            isExpandable: true,
            size: NODE_SIZES.awsVpc.collapsed,
            position: { x: 0, y: 0 },
            data: {
                type: 'aws-vpc',
                label: vpcId,
                level: 1,
                vpcId,
                awsAccountId: accountId,
                awsRegion: vpcGroup.region,
                isExpanded: false,
                isExpandable: true,
                isLoading,
                childCount: vpcGroup.resources.length,
                expansionDepth: 1
            }
        };
    }

    // Expanded VPC - group resources by subnet
    const subnetGroups = groupResourcesBySubnet(vpcGroup.resources);

    // Build subnet container nodes
    const children: LayoutNode[] = subnetGroups.map(subnetGroup =>
        buildSubnetLayoutNode(subnetGroup, vpcId, accountId)
    );

    // Position subnet containers in a variable grid
    positionNodesInVariableGrid(children, LAYOUT.nodeSpacing);

    // Calculate container size from positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.awsVpc.expanded
    );

    return {
        id: `vpc:${vpcId}`,
        type: 'container',
        isExpanded: true,
        isExpandable: true,
        size: containerSize,
        position: { x: 0, y: 0 },
        children,
        data: {
            type: 'container',
            containerType: 'aws-vpc',
            label: vpcId,
            level: 1,
            vpcId,
            awsAccountId: accountId,
            awsRegion: vpcGroup.region,
            count: vpcGroup.resources.length,
            isExpanded: true,
            isExpandable: true,
            expansionDepth: 1
        }
    };
}

// ============================================================================
// BUILD GLOBAL GROUP LAYOUT NODE
// ============================================================================

function buildGlobalGroupLayoutNode(
    vpcGroup: VPCResourceGroup,
    accountId: string
): LayoutNode {
    // Global resources are always shown expanded when account is expanded
    const children = buildAwsResourceLayoutNodes(vpcGroup.resources);

    // Position children first
    children.forEach((child, index) => {
        child.position = calculateGridPosition(
            index,
            vpcGroup.resources.length,
            NODE_SIZES.awsResource,
            LAYOUT.nodeSpacing
        );
    });

    // Calculate container size from positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.awsGlobalGroup.expanded
    );

    return {
        id: `global:${accountId}`,
        type: 'container',
        isExpanded: true,
        isExpandable: false, // Global group can't be collapsed individually
        size: containerSize,
        position: { x: 0, y: 0 },
        children,
        data: {
            type: 'container',
            containerType: 'aws-global',
            label: 'Global Resources',
            level: 1,
            awsAccountId: accountId,
            count: children.length,
            isExpanded: true,
            isExpandable: false,
            expansionDepth: 1
        }
    };
}

// ============================================================================
// CALCULATE CONTAINER SIZE FROM POSITIONED CHILDREN
// ============================================================================

function calculateContainerSizeFromPositionedChildren(
    children: LayoutNode[],
    minSize: { minWidth: number; minHeight: number }
): { width: number; height: number } {
    if (children.length === 0) {
        return { width: minSize.minWidth, height: minSize.minHeight };
    }

    // Find the bounding box of all positioned children
    let maxX = 0;
    let maxY = 0;

    children.forEach(child => {
        const rightEdge = child.position.x + child.size.width;
        const bottomEdge = child.position.y + child.size.height;
        maxX = Math.max(maxX, rightEdge);
        maxY = Math.max(maxY, bottomEdge);
    });

    // Add padding at the bottom and right
    const totalWidth = maxX + LAYOUT.containerPadding;
    const totalHeight = maxY + LAYOUT.containerPadding;

    return {
        width: Math.max(minSize.minWidth, totalWidth),
        height: Math.max(minSize.minHeight, totalHeight)
    };
}

// ============================================================================
// BUILD AWS ACCOUNT LAYOUT NODE
// ============================================================================

function buildAwsAccountLayoutNode(
    connection: AWSConnection,
    isExpanded: boolean,
    accountCache: AwsAccountCacheEntry | undefined,
    viewState: ExpansionViewState,
    loadingNodes: Set<string>
): LayoutNode {
    const accountId = connection.account_id;
    const isLoading = loadingNodes.has(`aws-account:${accountId}`);

    if (!isExpanded || !accountCache?.isComplete) {
        // Collapsed AWS account
        return {
            id: `aws-account:${accountId}`,
            type: 'aws-account',
            isExpanded: false,
            isExpandable: true,
            size: NODE_SIZES.awsAccount.collapsed,
            position: { x: 0, y: 0 },
            data: {
                type: 'aws-account',
                label: `AWS ${accountId}`,
                level: 0,
                awsAccountId: accountId,
                awsRegion: connection.region,
                connectionStatus: connection.connection_status,
                isExpanded: false,
                isExpandable: true,
                isLoading,
                childCount: accountCache?.resources?.length,
                expansionDepth: 0
            }
        };
    }

    // Expanded AWS account - build VPC and global resource children
    const childNodes: LayoutNode[] = [];
    const vpcGroups = accountCache.vpcGroups || [];

    // Add global resources group first (if any)
    const globalGroup = vpcGroups.find(g => g.isGlobal);
    if (globalGroup && globalGroup.resources.length > 0) {
        childNodes.push(buildGlobalGroupLayoutNode(globalGroup, accountId));
    }

    // Add VPC groups (actual VPCs, not global)
    const actualVpcs = vpcGroups.filter(g => !g.isGlobal);
    actualVpcs.forEach(vpcGroup => {
        const isVpcExpanded = viewState.expandedVpcs.has(vpcGroup.vpcId);
        const vpcLoading = loadingNodes.has(`vpc:${vpcGroup.vpcId}`);
        childNodes.push(buildVpcLayoutNode(vpcGroup, isVpcExpanded, accountId, vpcLoading));
    });

    // Position children in grid with variable sizes
    positionNodesInVariableGrid(childNodes, LAYOUT.namespaceSpacing);

    // Calculate container size from actual positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        childNodes,
        NODE_SIZES.awsAccount.expanded
    );

    // Count only actual VPCs (not the global resources group)
    const vpcCount = actualVpcs.length;

    return {
        id: `aws-account:${accountId}`,
        type: 'container',
        isExpanded: true,
        isExpandable: true,
        size: containerSize,
        position: { x: 0, y: 0 },
        children: childNodes,
        data: {
            type: 'container',
            containerType: 'aws-account',
            label: `AWS ${accountId}`,
            level: 0,
            awsAccountId: accountId,
            awsRegion: connection.region,
            connectionStatus: connection.connection_status,
            count: vpcCount,
            isExpanded: true,
            isExpandable: true,
            expansionDepth: 0
        }
    };
}

// ============================================================================
// BUILD OCI RESOURCE LAYOUT NODES
// ============================================================================

function buildOciResourceLayoutNodes(resources: any[]): LayoutNode[] {
    if (resources.length === 0) return [];

    const layoutNodes: LayoutNode[] = [];

    resources.forEach((resource, index) => {
        const position = calculateGridPosition(
            index,
            resources.length,
            NODE_SIZES.ociResource,
            LAYOUT.nodeSpacing
        );

        layoutNodes.push({
            id: resource.id,
            type: 'oci-resource',
            isExpanded: false,
            isExpandable: false,
            size: NODE_SIZES.ociResource,
            position,
            data: {
                type: 'oci-resource',
                label: resource.name || resource.resource_id,
                level: 2,
                resourceType: resource.resource_type,
                resourceId: resource.resource_id,
                ocid: resource.resource_id, // OCI OCID is the resource_id
                ociTenancyOcid: resource.oci_tenancy_ocid, // Direct field for OCI
                ociRegion: resource.region,
                vcnId: resource.vpc_id, // VCN stored in vpc_id
                subnetId: resource.subnet_id,
                availabilityDomain: resource.availability_zone,
                privateIp: resource.private_ip,
                publicIp: resource.public_ip,
                resourceStatus: resource.resource_status,
                compartmentId: resource.compartment_id,
                isExpandable: false,
                expansionDepth: 2,
                // Incident data (from API response)
                incidentCount: resource.incident_count,
                incidentSeverity: resource.incident_severity,
                // Full CloudResource for detail dialog
                cloudResource: resource
            }
        });
    });

    return layoutNodes;
}

// ============================================================================
// BUILD OCI SUBNET LAYOUT NODE
// ============================================================================

function buildOciSubnetLayoutNode(
    subnetGroup: SubnetResourceGroup,
    vcnId: string,
    tenancyOcid: string
): LayoutNode {
    const subnetId = subnetGroup.subnetId;
    const isNoSubnet = subnetId === 'no-subnet';

    // Build resource children for this subnet
    const children = buildOciResourceLayoutNodes(subnetGroup.resources);

    // Position children in grid
    children.forEach((child, index) => {
        child.position = calculateGridPosition(
            index,
            subnetGroup.resources.length,
            NODE_SIZES.ociResource,
            LAYOUT.nodeSpacing
        );
    });

    // Calculate container size from positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.ociSubnet.expanded
    );

    // Display name: show subnet ID shortened or "VCN Resources" for no-subnet
    const displayLabel = isNoSubnet
        ? 'VCN Resources'
        : subnetId.length > 20 ? subnetId.substring(0, 17) + '...' : subnetId;

    return {
        id: `oci-subnet:${vcnId}:${subnetId}`,
        type: 'container',
        isExpanded: true,
        isExpandable: false, // Subnets are always expanded
        size: containerSize,
        position: { x: 0, y: 0 },
        children,
        data: {
            type: 'container',
            containerType: 'oci-subnet',
            label: displayLabel,
            level: 2,
            subnetId: isNoSubnet ? undefined : subnetId,
            vcnId,
            ociTenancyOcid: tenancyOcid,
            availabilityDomain: subnetGroup.availabilityZone,
            count: children.length,
            isExpanded: true,
            isExpandable: false,
            expansionDepth: 2
        }
    };
}

// ============================================================================
// BUILD VCN LAYOUT NODE
// ============================================================================

function buildVcnLayoutNode(
    vcnGroup: VCNResourceGroup,
    isExpanded: boolean,
    tenancyOcid: string,
    isLoading: boolean
): LayoutNode {
    const vcnId = vcnGroup.vcnId;

    if (!isExpanded) {
        // Collapsed VCN
        return {
            id: `vcn:${vcnId}`,
            type: 'oci-vcn',
            isExpanded: false,
            isExpandable: true,
            size: NODE_SIZES.ociVcn.collapsed,
            position: { x: 0, y: 0 },
            data: {
                type: 'oci-vcn',
                label: vcnGroup.vcnName || vcnId,
                level: 1,
                vcnId,
                vcnName: vcnGroup.vcnName,
                ociTenancyOcid: tenancyOcid,
                ociRegion: vcnGroup.region,
                isExpanded: false,
                isExpandable: true,
                isLoading,
                childCount: vcnGroup.resources.length,
                expansionDepth: 1
            }
        };
    }

    // Expanded VCN - group resources by subnet
    const subnetGroups = groupResourcesBySubnet(vcnGroup.resources);

    // Separate actual subnets from VCN-level resources (no-subnet)
    const actualSubnets = subnetGroups.filter(g => g.subnetId !== 'no-subnet');
    const vcnLevelGroup = subnetGroups.find(g => g.subnetId === 'no-subnet');

    // Build children: subnet containers + VCN-level resources directly
    const children: LayoutNode[] = [];

    // Add subnet container nodes
    actualSubnets.forEach(subnetGroup => {
        children.push(buildOciSubnetLayoutNode(subnetGroup, vcnId, tenancyOcid));
    });

    // Add VCN-level resources directly (no container wrapper)
    if (vcnLevelGroup && vcnLevelGroup.resources.length > 0) {
        const vcnLevelNodes = buildOciResourceLayoutNodes(vcnLevelGroup.resources);
        children.push(...vcnLevelNodes);
    }

    // Position all children (subnet containers + direct resources) in a variable grid
    positionNodesInVariableGrid(children, LAYOUT.nodeSpacing);

    // Calculate container size from positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.ociVcn.expanded
    );

    return {
        id: `vcn:${vcnId}`,
        type: 'container',
        isExpanded: true,
        isExpandable: true,
        size: containerSize,
        position: { x: 0, y: 0 },
        children,
        data: {
            type: 'container',
            containerType: 'oci-vcn',
            label: vcnGroup.vcnName || vcnId,
            level: 1,
            vcnId,
            vcnName: vcnGroup.vcnName,
            ociTenancyOcid: tenancyOcid,
            ociRegion: vcnGroup.region,
            count: vcnGroup.resources.length,
            isExpanded: true,
            isExpandable: true,
            expansionDepth: 1
        }
    };
}

// ============================================================================
// BUILD OCI GLOBAL GROUP LAYOUT NODE
// ============================================================================

function buildOciGlobalGroupLayoutNode(
    vcnGroup: VCNResourceGroup,
    tenancyOcid: string
): LayoutNode {
    // Global resources are always shown expanded when tenancy is expanded
    const children = buildOciResourceLayoutNodes(vcnGroup.resources);

    // Position children first
    children.forEach((child, index) => {
        child.position = calculateGridPosition(
            index,
            vcnGroup.resources.length,
            NODE_SIZES.ociResource,
            LAYOUT.nodeSpacing
        );
    });

    // Calculate container size from positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        children,
        NODE_SIZES.ociGlobalGroup.expanded
    );

    return {
        id: `oci-global:${tenancyOcid}`,
        type: 'container',
        isExpanded: true,
        isExpandable: false, // Global group can't be collapsed individually
        size: containerSize,
        position: { x: 0, y: 0 },
        children,
        data: {
            type: 'container',
            containerType: 'oci-global',
            label: 'Global Resources',
            level: 1,
            ociTenancyOcid: tenancyOcid,
            count: children.length,
            isExpanded: true,
            isExpandable: false,
            expansionDepth: 1
        }
    };
}

// ============================================================================
// BUILD OCI TENANCY LAYOUT NODE
// ============================================================================

function buildOciTenancyLayoutNode(
    connection: OCIConnection,
    isExpanded: boolean,
    tenancyCache: OciTenancyCacheEntry | undefined,
    viewState: ExpansionViewState,
    loadingNodes: Set<string>
): LayoutNode {
    const tenancyOcid = connection.tenancy_ocid;
    const isLoading = loadingNodes.has(`oci-tenancy:${tenancyOcid}`);

    if (!isExpanded || !tenancyCache?.isComplete) {
        // Collapsed OCI tenancy
        return {
            id: `oci-tenancy:${tenancyOcid}`,
            type: 'oci-tenancy',
            isExpanded: false,
            isExpandable: true,
            size: NODE_SIZES.ociTenancy.collapsed,
            position: { x: 0, y: 0 },
            data: {
                type: 'oci-tenancy',
                label: connection.connection_name || `OCI ${tenancyOcid.substring(0, 20)}...`,
                level: 0,
                ociTenancyOcid: tenancyOcid,
                ociConnectionId: connection.id,
                ociRegion: connection.region,
                connectionStatus: connection.connection_status,
                isExpanded: false,
                isExpandable: true,
                isLoading,
                childCount: tenancyCache?.resources?.length,
                expansionDepth: 0
            }
        };
    }

    // Expanded OCI tenancy - build VCN and global resource children
    const childNodes: LayoutNode[] = [];
    const vcnGroups = tenancyCache.vcnGroups || [];

    // Add global resources group first (if any)
    const globalGroup = vcnGroups.find(g => g.isGlobal);
    if (globalGroup && globalGroup.resources.length > 0) {
        childNodes.push(buildOciGlobalGroupLayoutNode(globalGroup, tenancyOcid));
    }

    // Add VCN groups (actual VCNs, not global)
    const actualVcns = vcnGroups.filter(g => !g.isGlobal);
    actualVcns.forEach(vcnGroup => {
        const isVcnExpanded = viewState.expandedVcns.has(vcnGroup.vcnId);
        const vcnLoading = loadingNodes.has(`vcn:${vcnGroup.vcnId}`);
        childNodes.push(buildVcnLayoutNode(vcnGroup, isVcnExpanded, tenancyOcid, vcnLoading));
    });

    // Position children in grid with variable sizes
    positionNodesInVariableGrid(childNodes, LAYOUT.namespaceSpacing);

    // Calculate container size from actual positioned children
    const containerSize = calculateContainerSizeFromPositionedChildren(
        childNodes,
        NODE_SIZES.ociTenancy.expanded
    );

    // Count only actual VCNs (not the global resources group)
    const vcnCount = actualVcns.length;

    return {
        id: `oci-tenancy:${tenancyOcid}`,
        type: 'container',
        isExpanded: true,
        isExpandable: true,
        size: containerSize,
        position: { x: 0, y: 0 },
        children: childNodes,
        data: {
            type: 'container',
            containerType: 'oci-tenancy',
            label: connection.connection_name || `OCI ${tenancyOcid.substring(0, 20)}...`,
            level: 0,
            ociTenancyOcid: tenancyOcid,
            ociConnectionId: connection.id,
            ociRegion: connection.region,
            connectionStatus: connection.connection_status,
            vcnCount,
            count: vcnCount,
            isExpanded: true,
            isExpandable: true,
            expansionDepth: 0
        }
    };
}

// ============================================================================
// BUILD FULL LAYOUT TREE
// ============================================================================

function buildLayoutTree(input: LayoutEngineInput): LayoutNode[] {
    const {
        clusters,
        viewState,
        clusterCache,
        namespaceCache,
        loadingNodes,
        awsConnections = [],
        awsAccountCache = new Map(),
        ociConnections = [],
        ociTenancyCache = new Map()
    } = input;

    // Build all top-level nodes (clusters and AWS accounts)
    const topLevelNodes: LayoutNode[] = [];

    // Build cluster nodes
    clusters.forEach(cluster => {
        const isExpanded = viewState.expandedClusters.has(cluster.uid);
        const cache = clusterCache.get(cluster.uid);

        const clusterNode = buildClusterLayoutNode(
            cluster,
            isExpanded,
            cache,
            viewState,
            namespaceCache,
            loadingNodes
        );

        topLevelNodes.push(clusterNode);
    });

    // Build AWS account nodes (deduplicate by account_id, preferring Cloud Ingestion connections)
    // Cloud Ingestion connections have resource access, VPC Flow Logs connections only have flow data
    const accountConnections = new Map<string, typeof awsConnections[0]>();
    awsConnections.forEach(connection => {
        const existing = accountConnections.get(connection.account_id);
        const isCloudIngestion = connection.role_arn?.includes('CloudIngestion');
        const existingIsCloudIngestion = existing?.role_arn?.includes('CloudIngestion');

        if (!existing) {
            // First connection for this account
            accountConnections.set(connection.account_id, connection);
        } else if (isCloudIngestion && !existingIsCloudIngestion) {
            // Replace VPC Flow Logs with Cloud Ingestion (Cloud Ingestion has resource access)
            accountConnections.set(connection.account_id, connection);
        }
        // Otherwise keep existing (either both are same type, or existing is already Cloud Ingestion)
    });

    // Build nodes from deduplicated connections
    accountConnections.forEach(connection => {
        const isExpanded = viewState.expandedAwsAccounts.has(connection.account_id);
        const cache = awsAccountCache.get(connection.account_id);

        const accountNode = buildAwsAccountLayoutNode(
            connection,
            isExpanded,
            cache,
            viewState,
            loadingNodes
        );

        topLevelNodes.push(accountNode);
    });

    // Build OCI tenancy nodes (deduplicate by tenancy_ocid)
    const tenancyConnections = new Map<string, typeof ociConnections[0]>();
    ociConnections.forEach(connection => {
        const existing = tenancyConnections.get(connection.tenancy_ocid);
        if (!existing) {
            // First connection for this tenancy
            tenancyConnections.set(connection.tenancy_ocid, connection);
        }
        // Keep first connection (could add preference logic if needed later)
    });

    // Build nodes from deduplicated OCI connections
    tenancyConnections.forEach(connection => {
        const isExpanded = viewState.expandedOciTenancies.has(connection.tenancy_ocid);
        const cache = ociTenancyCache.get(connection.tenancy_ocid);

        const tenancyNode = buildOciTenancyLayoutNode(
            connection,
            isExpanded,
            cache,
            viewState,
            loadingNodes
        );

        topLevelNodes.push(tenancyNode);
    });

    // ========================================================================
    // EXTERNAL IP COLLECTION: Track which source(s) reference each IP
    // ========================================================================
    const MAX_INDIVIDUAL_EXTERNAL_IPS = 25;

    // IP -> set of source node IDs (cluster UIDs, AWS account IDs, OCI tenancy IDs)
    const ipToSources = new Map<string, Set<string>>();
    // IP -> known IP metadata (friendly name)
    const knownIpMeta = new Map<string, { ip: string; name: string }>();

    // Record an IP and its source
    const recordIp = (identifier: string, sourceId: string, knownName?: string) => {
        if (!identifier || identifier === 'reserved:world') return;
        if (!ipToSources.has(identifier)) {
            ipToSources.set(identifier, new Set());
        }
        ipToSources.get(identifier)!.add(sourceId);
        if (knownName) {
            knownIpMeta.set(identifier, { ip: identifier, name: knownName });
        }
    };

    // Helper function to process flows for external IPs and known IPs
    const processFlowsForExternalIPs = (flows: FlowSummary[], sourceId: string) => {
        flows.forEach((flow: FlowSummary) => {
            // Check source for external traffic
            if (flow.src_namespace === '' || flow.src_namespace === 'external' ||
                flow.src_kind === 'External' || flow.src_kind === 'Unmanaged') {
                const srcIdentifier = flow.src_ip || flow.src_name;
                if (srcIdentifier) {
                    recordIp(srcIdentifier, sourceId, flow.src_known_ip_name);
                }
            }
            // Check destination for external traffic
            if (flow.dst_namespace === '' || flow.dst_namespace === 'external' ||
                flow.dst_kind === 'External' || flow.dst_kind === 'Unmanaged') {
                const dstIdentifier = flow.dst_ip || flow.dst_name;
                if (dstIdentifier) {
                    recordIp(dstIdentifier, sourceId, flow.dst_known_ip_name);
                }
            }
        });
    };

    // Process AWS account flows
    viewState.expandedAwsAccounts.forEach(accountId => {
        const cache = awsAccountCache.get(accountId);
        if (cache?.flows) {
            processFlowsForExternalIPs(cache.flows, `aws-account:${accountId}`);
        }
    });

    // Process K8s cluster flows (cluster-level + all namespace-level)
    viewState.expandedClusters.forEach(clusterUID => {
        const clusterCache_entry = clusterCache.get(clusterUID);
        if (clusterCache_entry?.flows) {
            processFlowsForExternalIPs(clusterCache_entry.flows, clusterUID);
        }
        namespaceCache.forEach(nsCache => {
            if (nsCache?.clusterUID === clusterUID && nsCache?.flows) {
                processFlowsForExternalIPs(nsCache.flows, clusterUID);
            }
        });
    });

    // Process OCI tenancy flows
    viewState.expandedOciTenancies.forEach(tenancyOcid => {
        const cache = ociTenancyCache.get(tenancyOcid);
        if (cache?.flows) {
            processFlowsForExternalIPs(cache.flows, `oci-tenancy:${tenancyOcid}`);
        }
    });

    // ========================================================================
    // PARTITION IPs: source-specific (1 source) vs shared (2+ sources)
    // ========================================================================
    const sourceSpecificIps = new Map<string, {
        externalIps: string[];
        knownIps: Map<string, { ip: string; name: string }>;
    }>();
    const sharedExternalIps: string[] = [];
    const sharedKnownIps = new Map<string, { ip: string; name: string }>();

    ipToSources.forEach((sources, ip) => {
        const isKnown = knownIpMeta.has(ip);
        if (sources.size === 1) {
            const sourceId = Array.from(sources)[0];
            if (!sourceSpecificIps.has(sourceId)) {
                sourceSpecificIps.set(sourceId, { externalIps: [], knownIps: new Map() });
            }
            const group = sourceSpecificIps.get(sourceId)!;
            if (isKnown) {
                group.knownIps.set(ip, knownIpMeta.get(ip)!);
            } else {
                group.externalIps.push(ip);
            }
        } else {
            if (isKnown) {
                sharedKnownIps.set(ip, knownIpMeta.get(ip)!);
            } else {
                sharedExternalIps.push(ip);
            }
        }
    });

    // ========================================================================
    // BUILD LAYOUT NODES: helper to create external/known-ip nodes from a group
    // ========================================================================
    const buildExternalGroupNodes = (
        extIps: string[],
        knIps: Map<string, { ip: string; name: string }>,
        aggregateId?: string
    ): LayoutNode[] => {
        const nodes: LayoutNode[] = [];

        // Known IP nodes (always individual)
        knIps.forEach(({ ip, name }) => {
            nodes.push({
                id: `known-ip|${encodeURIComponent(ip)}`,
                type: 'known-ip',
                isExpanded: false,
                isExpandable: false,
                size: NODE_SIZES.knownIp,
                position: { x: 0, y: 0 },
                data: {
                    type: 'known-ip',
                    label: name,
                    level: 0,
                    externalIp: ip,
                    knownIpName: name,
                    isExternal: true,
                    isExpanded: false,
                    isExpandable: false,
                    expansionDepth: 0
                }
            });
        });

        // External IP nodes with per-group aggregation threshold
        if (extIps.length > 0) {
            if (extIps.length <= MAX_INDIVIDUAL_EXTERNAL_IPS) {
                extIps.forEach(ip => {
                    nodes.push({
                        id: `external-ip|${encodeURIComponent(ip)}`,
                        type: 'external-ip',
                        isExpanded: false,
                        isExpandable: false,
                        size: NODE_SIZES.externalIp,
                        position: { x: 0, y: 0 },
                        data: {
                            type: 'external-ip',
                            label: ip,
                            level: 0,
                            externalIp: ip,
                            isExternal: true,
                            isExpanded: false,
                            isExpandable: false,
                            expansionDepth: 0
                        }
                    });
                });
            } else {
                nodes.push({
                    id: aggregateId || 'external',
                    type: 'external-ip',
                    isExpanded: false,
                    isExpandable: false,
                    size: NODE_SIZES.externalIp,
                    position: { x: 0, y: 0 },
                    data: {
                        type: 'external-ip',
                        label: 'External',
                        level: 0,
                        externalIp: 'aggregate',
                        count: extIps.length,
                        isExternal: true,
                        isExpanded: false,
                        isExpandable: false,
                        expansionDepth: 0
                    }
                });
            }
        }

        return nodes;
    };

    // Build per-source external node groups
    const sourceExternalNodes = new Map<string, LayoutNode[]>();
    sourceSpecificIps.forEach((group, sourceId) => {
        const nodes = buildExternalGroupNodes(
            group.externalIps,
            group.knownIps,
            `external-agg|${sourceId}`
        );
        if (nodes.length > 0) {
            sourceExternalNodes.set(sourceId, nodes);
        }
    });

    // Build shared external nodes
    const sharedNodes = buildExternalGroupNodes(sharedExternalIps, sharedKnownIps, 'external');

    // ========================================================================
    // HELPER: Calculate column layout dimensions for source-specific IPs
    // ========================================================================
    const calculateExternalIpColumnsDimensions = (
        nodes: LayoutNode[],
        sourceHeight: number
    ): { totalWidth: number; maxNodeHeight: number; columns: { nodes: LayoutNode[]; x: number }[] } => {
        if (nodes.length === 0) return { totalWidth: 0, maxNodeHeight: 0, columns: [] };

        const maxNodeHeight = Math.max(...nodes.map(n => n.size.height));
        const maxNodeWidth = Math.max(...nodes.map(n => n.size.width));
        const itemsPerColumn = Math.max(1, Math.floor(sourceHeight / (maxNodeHeight + LAYOUT.nodeSpacing)));
        const numColumns = Math.ceil(nodes.length / itemsPerColumn);
        const gapBeforeFirst = LAYOUT.clusterSpacing;
        const gapBetweenCols = LAYOUT.nodeSpacing;

        const totalWidth = gapBeforeFirst
            + numColumns * maxNodeWidth
            + Math.max(0, numColumns - 1) * gapBetweenCols;

        const columns: { nodes: LayoutNode[]; x: number }[] = [];
        for (let c = 0; c < numColumns; c++) {
            const colNodes = nodes.slice(c * itemsPerColumn, (c + 1) * itemsPerColumn);
            const x = gapBeforeFirst + c * (maxNodeWidth + gapBetweenCols);
            columns.push({ nodes: colNodes, x });
        }

        return { totalWidth, maxNodeHeight, columns };
    };

    // ========================================================================
    // PHASE 1: Position shared external IPs at the TOP of the map
    // ========================================================================
    let sharedRowHeight = 0;
    if (sharedNodes.length > 0) {
        let sharedX = 0;
        sharedNodes.forEach(node => {
            node.position = { x: sharedX, y: 0 };
            node.absolutePosition = { x: sharedX, y: 0 };
            sharedX += node.size.width + LAYOUT.clusterSpacing;
            sharedRowHeight = Math.max(sharedRowHeight, node.size.height);
        });
        sharedRowHeight += LAYOUT.clusterSpacing; // gap between shared row and infrastructure
    }

    // ========================================================================
    // PHASE 2: Position infrastructure nodes with effective widths
    // ========================================================================
    const infraStartY = sharedRowHeight;
    let currentX = 0;
    let currentY = infraStartY;
    let maxRowHeight = 0;
    const ITEMS_PER_ROW = 3;

    // Pre-compute effective widths (node width + external IP columns width)
    const effectiveWidths: number[] = topLevelNodes.map(node => {
        const sideNodes = sourceExternalNodes.get(node.id);
        if (!sideNodes || sideNodes.length === 0) return node.size.width;
        const { totalWidth } = calculateExternalIpColumnsDimensions(sideNodes, node.size.height);
        return node.size.width + totalWidth;
    });

    topLevelNodes.forEach((node, index) => {
        if (index > 0 && index % ITEMS_PER_ROW === 0) {
            currentX = 0;
            currentY += maxRowHeight + LAYOUT.clusterSpacing;
            maxRowHeight = 0;
        }

        node.position = { x: currentX, y: currentY };
        node.absolutePosition = { x: currentX, y: currentY };

        currentX += effectiveWidths[index] + LAYOUT.clusterSpacing;
        maxRowHeight = Math.max(maxRowHeight, node.size.height);
    });

    // ========================================================================
    // PHASE 3: Position source-specific IPs to the RIGHT of their source
    // ========================================================================
    const allExternalNodes: LayoutNode[] = [];

    topLevelNodes.forEach(infraNode => {
        const sideNodes = sourceExternalNodes.get(infraNode.id);
        if (!sideNodes || sideNodes.length === 0) return;

        const infraAbsX = infraNode.absolutePosition?.x ?? infraNode.position.x;
        const infraAbsY = infraNode.absolutePosition?.y ?? infraNode.position.y;

        const { columns, maxNodeHeight } = calculateExternalIpColumnsDimensions(sideNodes, infraNode.size.height);

        columns.forEach(col => {
            col.nodes.forEach((node, rowIdx) => {
                const absX = infraAbsX + infraNode.size.width + col.x;
                const absY = infraAbsY + rowIdx * (maxNodeHeight + LAYOUT.nodeSpacing);
                node.position = { x: absX, y: absY };
                node.absolutePosition = { x: absX, y: absY };
            });
        });

        allExternalNodes.push(...sideNodes);
    });

    // Add shared nodes and source-specific nodes to the main array
    allExternalNodes.push(...sharedNodes);
    topLevelNodes.push(...allExternalNodes);

    return topLevelNodes;
}

// ============================================================================
// CALCULATE CANVAS BOUNDS
// ============================================================================

function calculateCanvasBounds(layoutNodes: LayoutNode[]): CanvasBounds {
    if (layoutNodes.length === 0) {
        return {
            minX: 0, minY: 0,
            maxX: 800, maxY: 600,
            width: 800, height: 600,
            centerX: 400, centerY: 300
        };
    }

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    const processNode = (node: LayoutNode, offsetX: number, offsetY: number) => {
        const absX = offsetX + node.position.x;
        const absY = offsetY + node.position.y;

        minX = Math.min(minX, absX);
        minY = Math.min(minY, absY);
        maxX = Math.max(maxX, absX + node.size.width);
        maxY = Math.max(maxY, absY + node.size.height);

        if (node.children) {
            node.children.forEach(child => processNode(child, absX, absY));
        }
    };

    layoutNodes.forEach(node => processNode(node, 0, 0));

    const width = maxX - minX;
    const height = maxY - minY;

    return {
        minX, minY, maxX, maxY,
        width, height,
        centerX: minX + width / 2,
        centerY: minY + height / 2
    };
}

// ============================================================================
// CONVERT LAYOUT TREE TO REACT FLOW NODES
// ============================================================================

function layoutTreeToReactFlowNodes(
    layoutNodes: LayoutNode[],
    loadingNodes: Set<string>
): Node<CustomNodeData>[] {
    const reactFlowNodes: Node<CustomNodeData>[] = [];

    const processNode = (
        layoutNode: LayoutNode,
        parentId?: string
    ) => {
        const isLoading = loadingNodes.has(layoutNode.id);

        // Build the React Flow node
        const rfNode: Node<CustomNodeData> = {
            id: layoutNode.id,
            type: layoutNode.type === 'container' ? 'container' : layoutNode.type,
            position: layoutNode.position,
            parentId,
            draggable: false,
            selectable: layoutNode.type !== 'container',
            style: {
                width: layoutNode.size.width,
                height: layoutNode.size.height
            },
            data: {
                ...layoutNode.data,
                isLoading
            } as CustomNodeData
        };

        reactFlowNodes.push(rfNode);

        // Process children
        if (layoutNode.children) {
            layoutNode.children.forEach(child => {
                processNode(child, layoutNode.id);
            });
        }
    };

    layoutNodes.forEach(node => processNode(node));

    return reactFlowNodes;
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useLayoutEngine(input: LayoutEngineInput): LayoutEngineOutput {
    const {
        clusters,
        viewState,
        clusterCache,
        namespaceCache,
        loadingNodes,
        awsConnections = [],
        awsAccountCache = new Map(),
        ociConnections = [],
        ociTenancyCache = new Map()
    } = input;

    // Serialize Sets to arrays for stable dependency comparison
    const expandedClustersArray = useMemo(
        () => Array.from(viewState.expandedClusters).sort(),
        [viewState.expandedClusters]
    );
    const expandedNamespacesArray = useMemo(
        () => Array.from(viewState.expandedNamespaces).sort(),
        [viewState.expandedNamespaces]
    );
    const expandedAwsAccountsArray = useMemo(
        () => Array.from(viewState.expandedAwsAccounts).sort(),
        [viewState.expandedAwsAccounts]
    );
    const expandedVpcsArray = useMemo(
        () => Array.from(viewState.expandedVpcs).sort(),
        [viewState.expandedVpcs]
    );
    const expandedOciTenanciesArray = useMemo(
        () => Array.from(viewState.expandedOciTenancies).sort(),
        [viewState.expandedOciTenancies]
    );
    const expandedVcnsArray = useMemo(
        () => Array.from(viewState.expandedVcns).sort(),
        [viewState.expandedVcns]
    );

    // Create stable cache references that only change when EXPANDED items have cache changes
    // This prevents layout recalculation when loading data for collapsed nodes
    const expandedClusterCacheKey = useMemo(() => {
        return expandedClustersArray.map(uid => {
            const entry = clusterCache.get(uid);
            return entry?.isComplete ? `${uid}:${entry.loadedAt}` : `${uid}:none`;
        }).join('|');
    }, [expandedClustersArray, clusterCache]);

    const expandedNamespaceCacheKey = useMemo(() => {
        return expandedNamespacesArray.map(id => {
            const entry = namespaceCache.get(id);
            return entry?.isComplete ? `${id}:${entry.loadedAt}` : `${id}:none`;
        }).join('|');
    }, [expandedNamespacesArray, namespaceCache]);

    const expandedAwsCacheKey = useMemo(() => {
        return expandedAwsAccountsArray.map(id => {
            const entry = awsAccountCache.get(id);
            return entry?.isComplete ? `${id}:${entry.loadedAt}` : `${id}:none`;
        }).join('|');
    }, [expandedAwsAccountsArray, awsAccountCache]);

    const expandedOciCacheKey = useMemo(() => {
        return expandedOciTenanciesArray.map(id => {
            const entry = ociTenancyCache.get(id);
            return entry?.isComplete ? `${id}:${entry.loadedAt}` : `${id}:none`;
        }).join('|');
    }, [expandedOciTenanciesArray, ociTenancyCache]);

    // Calculate layout positions - this should NOT depend on loadingNodes
    // to prevent flickering/jumping during loading state changes
    const { nodes: layoutNodes, canvasBounds } = useMemo(() => {
        // Construct input from the destructured values to avoid stale closure
        const layoutInput: LayoutEngineInput = {
            clusters,
            viewState,
            clusterCache,
            namespaceCache,
            loadingNodes: new Set(), // Don't use loading state for layout calculation
            awsConnections,
            awsAccountCache,
            ociConnections,
            ociTenancyCache,
        };

        // Build layout tree
        const layoutTree = buildLayoutTree(layoutInput);

        // Calculate canvas bounds
        const canvasBounds = calculateCanvasBounds(layoutTree);

        // Convert to React Flow nodes (without loading state)
        const nodes = layoutTreeToReactFlowNodes(layoutTree, new Set());

        return { nodes, canvasBounds };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clusters,
        viewState,
        expandedClustersArray,
        expandedNamespacesArray,
        expandedAwsAccountsArray,
        expandedVpcsArray,
        expandedOciTenanciesArray,
        expandedVcnsArray,
        // Use stable cache keys instead of Map references
        // This prevents recalculation when cache updates for collapsed nodes
        expandedClusterCacheKey,
        expandedNamespaceCacheKey,
        expandedAwsCacheKey,
        expandedOciCacheKey,
        awsConnections,
        ociConnections,
    ]);

    // Apply loading state to nodes separately - this updates frequently but doesn't affect positions
    const nodes = useMemo(() => {
        return layoutNodes.map(node => ({
            ...node,
            data: {
                ...node.data,
                isLoading: loadingNodes.has(node.id)
            }
        }));
    }, [layoutNodes, loadingNodes]);

    return { nodes, canvasBounds };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    buildLayoutTree,
    calculateCanvasBounds,
    layoutTreeToReactFlowNodes,
    calculateGridPosition,
    calculateContainerSize,
    getGridConfig,
    positionNodesInVariableGrid
};
